/**
 * DataResetPage — Owner-only one-click reset of distributor/vendor ledger
 * entries + set fresh opening balance. Linked to /admin/data-recovery for undo.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Buildings, CreditCard, Gift, DeviceMobile, Lightning, ArrowClockwise,
  Warning, ArrowCounterClockwise, FloppyDisk, FilePdf, Paperclip,
  UsersThree, ToggleRight, ToggleLeft,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';

function fmt(n) {
  if (typeof n !== 'number') n = Number(n || 0);
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const TABS = [
  { key: 'distributor', label: 'Distributors',         icon: Buildings,   color: 'blue'   },
  { key: 'card',        label: 'Card Vendors',         icon: CreditCard,  color: 'indigo' },
  { key: 'gift_card',   label: 'Gift Card Vendors',    icon: Gift,        color: 'purple' },
  { key: 'recharge',    label: 'Recharge Vendors',     icon: DeviceMobile,color: 'sky'    },
  { key: 'electricity', label: 'Electricity Vendors',  icon: Lightning,   color: 'amber'  },
];

function DataResetAccessDialog({ open, onClose }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/data-reset-access/');
      setList(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggle = async (uid, next) => {
    try {
      await api.patch(`/data-reset-access/${uid}`, { enabled: next });
      toast.success(next ? '✅ Access enabled' : '🚫 Access disabled');
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Toggle failed'); }
  };

  // group by role
  const cas = list.filter((u) => u.role === 'accountant');
  const assists = list.filter((u) => u.role === 'assistant');

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="dr-access-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><UsersThree size={20} /> Manage Data Reset Access</DialogTitle></DialogHeader>
        <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2 mb-2">
          💡 Admin/Owner ko hamesha access milta hai. CA & Assistants ko sirf tab access milega jab aap yahaan enable karoge. Default: OFF (safe).
        </div>
        {loading ? <div className="p-4 text-center text-sm text-gray-500">Loading...</div>
        : list.length === 0 ? <div className="p-4 text-center text-sm text-gray-500">Koi CA/Assistant nahi mila</div>
        : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {cas.length > 0 && (
              <div>
                <div className="text-[10px] uppercase font-bold text-gray-500 mb-1">CA / Accountants ({cas.length})</div>
                {cas.map((u) => (
                  <div key={u.user_id} className="flex items-center justify-between border-b py-2 px-1" data-testid={`dr-access-row-${u.user_id}`}>
                    <div>
                      <div className="font-medium text-sm">{u.name}</div>
                      <div className="text-[11px] text-gray-500">{u.email}</div>
                    </div>
                    <button onClick={() => toggle(u.user_id, !u.enabled)} className="flex items-center gap-1.5" data-testid={`dr-access-toggle-${u.user_id}`}>
                      {u.enabled
                        ? <><ToggleRight size={28} className="text-emerald-600" /><span className="text-xs text-emerald-700 font-medium">enabled</span></>
                        : <><ToggleLeft size={28} className="text-gray-400" /><span className="text-xs text-gray-500 font-medium">disabled</span></>}
                    </button>
                  </div>
                ))}
              </div>
            )}
            {assists.length > 0 && (
              <div>
                <div className="text-[10px] uppercase font-bold text-gray-500 mb-1 mt-3">Assistants ({assists.length})</div>
                {assists.map((u) => (
                  <div key={u.user_id} className="flex items-center justify-between border-b py-2 px-1" data-testid={`dr-access-row-${u.user_id}`}>
                    <div>
                      <div className="font-medium text-sm">{u.name}</div>
                      <div className="text-[11px] text-gray-500">{u.email}</div>
                    </div>
                    <button onClick={() => toggle(u.user_id, !u.enabled)} className="flex items-center gap-1.5" data-testid={`dr-access-toggle-${u.user_id}`}>
                      {u.enabled
                        ? <><ToggleRight size={28} className="text-emerald-600" /><span className="text-xs text-emerald-700 font-medium">enabled</span></>
                        : <><ToggleLeft size={28} className="text-gray-400" /><span className="text-xs text-gray-500 font-medium">disabled</span></>}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetConfirmDialog({ open, onClose, entity, entityType, onDone, prefill }) {
  const [amount, setAmount] = useState('');
  const [kind, setKind] = useState('advance');
  const [note, setNote] = useState('');
  const [openingDate, setOpeningDate] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount(prefill?.amount != null ? String(prefill.amount) : '');
      setKind(prefill?.kind || 'advance');
      setNote(prefill?.note || '');
      // Default opening_date: prefill (from PDF "as of") or today (YYYY-MM-DD)
      const today = new Date().toISOString().slice(0, 10);
      setOpeningDate(prefill?.as_of_date || today);
      setConfirmText('');
    }
  }, [open, prefill]);

  const labelAdvance = entityType === 'distributor'
    ? 'ADVANCE (distributor ne extra paid kiya — humein milne wala nahi)'
    : 'ADVANCE (humne vendor ko extra paid kiya — humein milne wala hai)';
  const labelPending = entityType === 'distributor'
    ? 'PENDING (distributor humein owe karta hai — recover karna hai)'
    : 'PENDING (hum vendor ko owe karte hain — pay karna hai)';

  const submit = async () => {
    if (!amount || Number(amount) < 0) { toast.error('Valid amount daalo'); return; }
    if (confirmText.trim().toUpperCase() !== 'RESET') { toast.error('Confirm field me "RESET" likho'); return; }
    setBusy(true);
    try {
      const r = await api.post('/data-reset/execute', {
        entity_type: entityType,
        entity_id: entity.id,
        opening_amount: Number(amount),
        kind,
        opening_date: openingDate || undefined,
        note,
        confirm: 'RESET',
      });
      toast.success(r.data.message || 'Reset done');
      onDone(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed');
    } finally { setBusy(false); }
  };

  if (!entity) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="reset-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Warning size={20} weight="bold" /> Reset {entity.name}?
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
            <div className="font-bold mb-1">⚠️ Ye saare entries soft-delete ho jayengi:</div>
            <ul className="list-disc pl-4 space-y-0.5">
              <li><b>{entity.entry_count}</b> ledger entries (current balance: {fmt(entity.current_balance)})</li>
              <li>Ek fresh "opening_balance" entry insert hogi neeche di gayi amount se</li>
              <li>30 din tak recovery available — Data Recovery page se restore kar sakte ho</li>
            </ul>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">Opening Balance Amount (₹)</label>
            <Input
              type="number" step="0.01" min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 7864"
              data-testid="reset-amount"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">
              Opening Date <span className="text-gray-500 font-normal">(default: today — change agar khatabook ka purana date use karna ho)</span>
            </label>
            <Input
              type="date"
              value={openingDate}
              onChange={(e) => setOpeningDate(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
              data-testid="reset-opening-date"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">Type</label>
            <div className="grid grid-cols-1 gap-1.5 mt-1">
              <label className={`flex items-start gap-2 border rounded p-2 cursor-pointer ${kind === 'advance' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'}`}>
                <input type="radio" checked={kind === 'advance'} onChange={() => setKind('advance')} data-testid="kind-advance" />
                <div className="text-xs">
                  <div className="font-semibold text-emerald-700">{labelAdvance}</div>
                </div>
              </label>
              <label className={`flex items-start gap-2 border rounded p-2 cursor-pointer ${kind === 'pending' ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}>
                <input type="radio" checked={kind === 'pending'} onChange={() => setKind('pending')} data-testid="kind-pending" />
                <div className="text-xs">
                  <div className="font-semibold text-red-700">{labelPending}</div>
                </div>
              </label>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">Note (optional)</label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Khatabook se imported" />
          </div>

          <div>
            <label className="text-xs font-semibold text-red-700">Type <b>RESET</b> to confirm <span className="text-gray-500 font-normal">(case-insensitive)</span></label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder='Type "RESET" (any case)'
              className={confirmText.trim().toUpperCase() === 'RESET' ? 'border-emerald-500' : 'border-red-300'}
              data-testid="reset-confirm-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || confirmText.trim().toUpperCase() !== 'RESET' || !amount}
            className="bg-red-600 hover:bg-red-700 gap-1"
            data-testid="reset-submit"
          >
            <FloppyDisk size={14} /> {busy ? 'Resetting…' : 'Reset & Set Opening'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DataResetPage() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [activeTab, setActiveTab] = useState('distributor');
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetTarget, setResetTarget] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [showAccess, setShowAccess] = useState(false);
  const fileInputRef = useRef(null);
  const rowFileInputRef = useRef(null);
  const [pendingEntityForUpload, setPendingEntityForUpload] = useState(null);
  const [parsing, setParsing] = useState(false);

  // Access check
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/data-reset-access/me');
        setMeta(r.data);
      } catch { setMeta({ can_access: false }); }
    })();
  }, []);

  const load = useCallback(async (type) => {
    setLoading(true);
    try {
      const r = await api.get(`/data-reset/entities/${type}`);
      setEntities(r.data || []);
    } catch (err) {
      if (err?.response?.status !== 403) toast.error(err?.response?.data?.detail || 'Load failed');
      setEntities([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (meta?.can_access) load(activeTab); }, [activeTab, meta?.can_access, load]);

  // PDF upload handler — top-level "Upload Khatabook PDF" → fuzzy-match → open dialog
  const handlePdfUpload = async (file, forceEntity) => {
    if (!file) return;
    setParsing(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('entity_type', activeTab);
      const r = await api.post('/data-reset/parse-khatabook', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { customer_name, net_amount, kind, matched_entity, cr_dr, as_of_date } = r.data;
      // Use force-entity if explicitly passed (per-row upload)
      let entity = forceEntity;
      if (!entity) {
        if (matched_entity) {
          entity = entities.find((e) => e.id === matched_entity.id);
        }
      }
      if (!entity) {
        toast.error(`PDF se "${customer_name}" name nikla, but match nahi mila ${activeTab} list mein. Manual select karo.`);
        setParsing(false);
        return;
      }
      const dateMsg = as_of_date ? ` (as of ${as_of_date})` : '';
      toast.success(`✅ ${customer_name}: ₹${net_amount.toLocaleString('en-IN')} ${cr_dr} → ${kind.toUpperCase()}${dateMsg}`);
      setPrefill({
        amount: net_amount,
        kind,
        as_of_date,
        note: `Imported from KhataBook PDF: ${file.name}${as_of_date ? ` (as of ${as_of_date})` : ''}`,
      });
      setResetTarget(entity);
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      if (err?.response?.status === 409 && detail.includes('BULK_FORMAT_DETECTED')) {
        toast.info('Ye Customer-List PDF hai (bulk). Bulk Import page khol raha hun...');
        // Stash the file in sessionStorage so bulk page can auto-parse it
        try {
          const reader = new FileReader();
          reader.onload = () => {
            sessionStorage.setItem('pending_bulk_pdf', JSON.stringify({
              name: file.name,
              type: file.type,
              data: reader.result,  // dataURL
              entity_type: activeTab,
            }));
            window.location.href = '/admin/bulk-khatabook';
          };
          reader.readAsDataURL(file);
        } catch {
          window.location.href = '/admin/bulk-khatabook';
        }
        return;
      }
      toast.error(detail || 'PDF parse failed');
    } finally { setParsing(false); }
  };

  if (!meta) return <div className="p-8 text-center text-sm text-gray-500">Loading...</div>;
  if (!meta.can_access) {
    return (
      <div className="max-w-md mx-auto mt-12 p-6 bg-red-50 border border-red-200 rounded-lg text-center" data-testid="dr-no-access">
        <Warning size={32} weight="duotone" className="mx-auto text-red-500 mb-2" />
        <div className="font-semibold text-red-900">Access Denied</div>
        <p className="text-sm text-red-700 mt-1">Data Reset feature ka access nahi hai. Admin se enable karwao. (Role: {user?.role})</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="data-reset-page">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ArrowClockwise size={26} weight="duotone" className="text-red-600" /> Data Reset & Opening Balance
          {meta.is_admin
            ? <span className="text-xs font-normal bg-red-100 text-red-700 px-2 py-0.5 rounded ml-2">ADMIN</span>
            : meta.is_ca
              ? <span className="text-xs font-normal bg-blue-100 text-blue-700 px-2 py-0.5 rounded ml-2">CA — ACCESS GRANTED</span>
              : <span className="text-xs font-normal bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded ml-2">ACCESS GRANTED</span>}
        </h1>
        <div className="ml-auto flex gap-2 flex-wrap">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; handlePdfUpload(f); }}
          />
          <input
            ref={rowFileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; handlePdfUpload(f, pendingEntityForUpload); setPendingEntityForUpload(null); }}
          />
          <Button
            size="sm"
            variant="outline"
            className="gap-1 text-fuchsia-700 border-fuchsia-200 hover:bg-fuchsia-50"
            disabled={parsing}
            onClick={() => fileInputRef.current?.click()}
            data-testid="upload-khatabook-pdf"
            title="Ek customer ka detailed transactions PDF (Net Balance auto-extract hoga)"
          >
            <FilePdf size={14} /> {parsing ? 'Parsing…' : 'Single PDF (1 customer)'}
          </Button>
          <Link to="/admin/bulk-khatabook">
            <Button
              size="sm"
              variant="outline"
              className="gap-1 text-fuchsia-700 border-fuchsia-200 hover:bg-fuchsia-50"
              data-testid="bulk-pdf-link"
              title="Saare customers ek hi PDF mein (Customer-List PDF)"
            >
              <FilePdf size={14} /> Bulk PDF (all customers)
            </Button>
          </Link>
          <Link to="/admin/data-recovery">
            <Button size="sm" variant="outline" className="gap-1" data-testid="recovery-link">
              <ArrowCounterClockwise size={14} /> Data Recovery
            </Button>
          </Link>
          {meta.can_manage_access && (
            <Button size="sm" variant="outline" className="gap-1" onClick={() => setShowAccess(true)} data-testid="dr-access-btn">
              <UsersThree size={14} /> Manage Access
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => load(activeTab)} className="gap-1">
            <ArrowClockwise size={14} /> Refresh
          </Button>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
        💡 <b>Old data entry karna hai?</b> 2 ways:
        <ul className="list-disc pl-5 mt-1 space-y-0.5">
          <li><b>Single PDF</b> — top-right button, ek customer ka detailed Khatabook transactions PDF upload karo. Net Balance auto-extract, vendor match karke dialog pre-fill ho jayega.</li>
          <li><b>Bulk PDF</b> — top-right button, KhataBook "Customer List" PDF (saare customers ek file mein) upload karo. Bulk import page pe sab matched rows ek click mein apply.</li>
          <li><b>Per-row</b> 📎 button — specific vendor ke liye single PDF upload (fuzzy-match skip).</li>
        </ul>
        <span className="text-[11px] text-amber-700 mt-1 block">Each reset creates a recovery batch — 30 din tak undo kar sakte ho. Galat PDF aa gyi to system auto-detect karke sahi page redirect kar dega.</span>
      </div>

      {/* Category Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = activeTab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              className={`px-3 py-2 text-xs font-semibold whitespace-nowrap rounded-t-md border-b-2 transition flex items-center gap-1 ${
                isActive
                  ? `border-${t.color}-600 text-${t.color}-700 bg-${t.color}-50`
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              data-testid={`tab-${t.key}`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        {loading ? <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
        : entities.length === 0 ? <div className="p-12 text-center text-sm text-gray-500">No entities found in this category.</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-right">Entries</th>
                  <th className="px-3 py-2 text-right">Credit</th>
                  <th className="px-3 py-2 text-right">Debit</th>
                  <th className="px-3 py-2 text-right">Current Balance</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {entities.map((e) => (
                  <tr key={e.id} className="border-t hover:bg-gray-50" data-testid={`entity-row-${e.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold">{e.name}</div>
                      {e.email && <div className="text-[11px] text-gray-500">{e.email}</div>}
                    </td>
                    <td className="px-3 py-2 text-right">{e.entry_count}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">{fmt(e.credit)}</td>
                    <td className="px-3 py-2 text-right text-red-700">{fmt(e.debit)}</td>
                    <td className={`px-3 py-2 text-right font-bold ${e.current_balance >= 0 ? 'text-fuchsia-700' : 'text-amber-700'}`}>{fmt(e.current_balance)}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-fuchsia-700 border-fuchsia-200 hover:bg-fuchsia-50"
                          onClick={() => { setPendingEntityForUpload(e); rowFileInputRef.current?.click(); }}
                          title="Khatabook PDF upload karo iss vendor ke liye"
                          data-testid={`upload-pdf-${e.id}`}
                        >
                          <Paperclip size={12} />
                        </Button>
                        <Button
                          size="sm"
                          className="bg-red-600 hover:bg-red-700 gap-1 h-7"
                          onClick={() => { setPrefill(null); setResetTarget(e); }}
                          data-testid={`reset-btn-${e.id}`}
                        >
                          <ArrowClockwise size={12} weight="bold" /> Reset & Set Opening
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ResetConfirmDialog
        open={!!resetTarget}
        onClose={() => { setResetTarget(null); setPrefill(null); }}
        entity={resetTarget}
        entityType={activeTab}
        prefill={prefill}
        onDone={() => { setResetTarget(null); setPrefill(null); load(activeTab); }}
      />

      {meta.can_manage_access && <DataResetAccessDialog open={showAccess} onClose={() => setShowAccess(false)} />}
    </div>
  );
}
